/**
 * useTurnstileWebRenderer
 *
 * Web-specific Turnstile widget rendering logic extracted from TurnstileWidget.tsx.
 * Handles script loading, widget rendering, and cleanup for the web platform.
 *
 * @see TurnstileWidget.tsx - Parent component that uses this hook
 */
import { useEffect, useState, type RefObject, type MutableRefObject } from 'react';
import { Platform } from 'react-native';
import {
  validateSiteKey,
  isTurnstileScriptLoaded,
  isTurnstileScriptPending,
  TURNSTILE_SCRIPT_URL,
} from '../lib/turnstile/turnstileConfig';

interface TurnstileRenderConfig {
  theme: string;
  size: string;
  appearance: string;
  execution: string;
  refreshExpired: string;
  retry: string;
  language?: string;
  action: string;
  cData?: string;
}

interface UseTurnstileWebRendererOptions {
  siteKey: string;
  containerRef: RefObject<HTMLDivElement | null>;
  widgetIdRef: MutableRefObject<string | null>;
  tokenReceivedRef: MutableRefObject<boolean>;
  onSuccess: (token: string) => void;
  onError?: (error: string) => void;
  onExpire?: () => void;
  onTimeout?: () => void;
  config: TurnstileRenderConfig;
}

interface UseTurnstileWebRendererResult {
  loading: boolean;
  error: string | null;
}

/**
 * Hook that manages Turnstile widget rendering on the web platform.
 *
 * Handles: script loading, widget rendering, duplicate prevention, and cleanup.
 * Only active on web — returns loading=false, error=null on native platforms.
 */
export function useTurnstileWebRenderer({
  siteKey,
  containerRef,
  widgetIdRef,
  tokenReceivedRef,
  onSuccess,
  onError,
  onExpire,
  onTimeout,
  config,
}: UseTurnstileWebRendererOptions): UseTurnstileWebRendererResult {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Render Turnstile widget into the container div
  const renderWidget = () => {
    if (Platform.OS !== 'web' || !containerRef.current) {
      return;
    }

    // Prevent duplicate rendering (use ref to avoid stale closure)
    if (widgetIdRef.current) {
      console.log('[TurnstileWebRenderer] Widget already rendered with ID:', widgetIdRef.current);
      return;
    }

    try {
      const turnstile = (window as any).turnstile;

      if (!turnstile) {
        throw new Error('Turnstile API not available');
      }

      console.log('[TurnstileWebRenderer] Rendering widget in container');
      const id = turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: (token: string) => {
          console.log('[TurnstileWebRenderer] Token received');
          tokenReceivedRef.current = true;
          setLoading(false);
          onSuccess(token);
        },
        'error-callback': (err: string) => {
          // DRAMA-WEB-28: Ignore post-success errors (e.g., 300030 widget hang)
          if (tokenReceivedRef.current) {
            console.warn('[TurnstileWebRenderer] Ignoring post-success error:', err);
            return;
          }
          console.error('[TurnstileWebRenderer] Challenge failed:', err);
          setLoading(false);
          setError(err);
          onError?.(err);
        },
        'expired-callback': () => {
          console.log('[TurnstileWebRenderer] Token expired');
          tokenReceivedRef.current = false; // Token no longer valid, allow errors again
          setLoading(true);
          onExpire?.();
        },
        'timeout-callback': () => {
          // DRAMA-WEB-28: Ignore post-success timeouts
          if (tokenReceivedRef.current) {
            console.warn('[TurnstileWebRenderer] Ignoring post-success timeout');
            return;
          }
          console.log('[TurnstileWebRenderer] Interactive challenge timeout');
          setLoading(false);
          onTimeout?.();
        },
        theme: config.theme,
        size: config.size,
        appearance: config.appearance,
        execution: config.execution,
        'refresh-expired': config.refreshExpired,
        retry: config.retry,
        ...(config.language && { language: config.language }),
        action: config.action,
        ...(config.cData && { cData: config.cData }),
      });

      widgetIdRef.current = id;
      setLoading(false);
      console.log('[TurnstileWebRenderer] Widget rendered:', id);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to render widget';
      console.error('[TurnstileWebRenderer]', errorMsg);
      setError(errorMsg);
      setLoading(false);
      onError?.(errorMsg);
    }
  };

  // Validate site key exists
  useEffect(() => {
    const validationError = validateSiteKey(siteKey);
    if (validationError) {
      console.error('[TurnstileWebRenderer]', validationError);
      setError(validationError);
      onError?.(validationError);
    }
  }, [siteKey, onError]);

  // Load Turnstile script and render widget (web only)
  useEffect(() => {
    if (Platform.OS !== 'web' || !containerRef.current) {
      return;
    }

    // Prevent duplicate rendering (use ref to avoid stale closure in empty-deps useEffect)
    if (widgetIdRef.current) {
      console.log('[TurnstileWebRenderer] Widget already rendered, skipping');
      return;
    }

    // Check if script already loaded
    if (isTurnstileScriptLoaded()) {
      console.log('[TurnstileWebRenderer] Script already loaded, rendering widget');
      renderWidget();
      return;
    }

    // Check if script is already being loaded
    if (isTurnstileScriptPending()) {
      console.log('[TurnstileWebRenderer] Script already loading, waiting for load event');
      const existingScript = document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]');
      existingScript?.addEventListener('load', () => {
        if (!widgetIdRef.current) {
          renderWidget();
        }
      });
      return;
    }

    // Load Turnstile script dynamically
    console.log('[TurnstileWebRenderer] Loading Turnstile script');
    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT_URL;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      console.log('[TurnstileWebRenderer] Script loaded');
      if (!widgetIdRef.current) {
        renderWidget();
      }
    };

    script.onerror = () => {
      // CSP must whitelist https://challenges.cloudflare.com in script-src.
      // If still blocked (e.g., ad blocker), degrade gracefully.
      console.warn('[TurnstileWebRenderer] Turnstile script failed to load — check CSP script-src or browser extensions');
      setError('Verification unavailable');
      setLoading(false);
      onError?.('Failed to load Turnstile script');
    };

    document.head.appendChild(script);

    return () => {
      // Cleanup: Remove widget if it exists
      // Uses widgetIdRef (not state) to avoid stale closure in empty-deps useEffect
      if (widgetIdRef.current && (window as any).turnstile) {
        try {
          console.log('[TurnstileWebRenderer] Cleaning up widget:', widgetIdRef.current);
          (window as any).turnstile.remove(widgetIdRef.current);
        } catch (err) {
          console.warn('[TurnstileWebRenderer] Failed to remove widget:', err);
        }
      }
    };
  }, []); // Empty deps - only run once on mount

  return { loading, error };
}
